import { defineStore } from 'pinia'
import usePromotionsStore from './promotions'
import useRelishStore from './relish'
import useUserStore from './user'
import { gaEventOnUserSignOut } from '~/composables/analytics'
import { useResetStores } from '~/composables/store'
import { STORE_ID } from '~/constants/store'
import type { LoginResponse, UserResponse } from '~/types/api/data-contracts'
import type { AuthState as State } from '~/types/auth'
import useNavigationMenuStore from './navigationMenu'

const initialState = (): State => ({
  authSession: undefined,
  authUser: undefined
})

/**
 * @description Store for auth data - saved in local storage.
 */
const useAuthStore = defineStore(STORE_ID.AUTH, {
  state: initialState,

  getters: {
    getAuthSession(): LoginResponse | undefined {
      return this.authSession
    },
    getAuthUser(): UserResponse | undefined {
      return this.authUser
    },
    isAuthenticated(): boolean {
      return !!this.authSession
    },
    isGuestUser(): boolean {
      const userStore = useUserStore()

      return !this.authSession && userStore.hasOnboarded
    }
  },

  actions: {
    async signIn(authSession: LoginResponse, authUser: UserResponse): Promise<void> {
      const { isWebMode } = useAppMode()
      const promotionsStore = usePromotionsStore()
      const userNavigationMenuStore = useNavigationMenuStore()
      const relishStore = useRelishStore()
      const userStore = useUserStore()
      const tracking = useTracking()

      this.authUser = authUser
      // replace the authSession with the new one, if it exists
      if (authSession && authSession.refreshToken) {
        this.authSession = authSession
      }

      userStore.details = undefined

      // Set up user tracking
      tracking.identifyUser(this.authUser)

      // Handle async requests
      const requests = []

      requests.push(promotionsStore.fetchPromotions())
      requests.push(relishStore.fetchEightAndDonate())
      requests.push(userNavigationMenuStore.fetchNavigationMenu())

      if (isWebMode) {
        emitSignedInState()
        requests.push(relishStore.fetchLoyaltyTags())
      } else {
        const { setupMobileNotificationsForRelishUser } = useSalesforceNotifications()

        requests.push(setupMobileNotificationsForRelishUser(this.authUser.salesforceContactId ?? ''))
      }

      await Promise.allSettled(requests)
    },
    setAuthUser(authUser: UserResponse): void {
      this.authUser = authUser
    },
    updateUserAndSession(authSession: LoginResponse, authUser: UserResponse): void {
      // Set up user tracking
      const tracking = useTracking()
      tracking.identifyUser(this.authUser)
      this.authUser = authUser
      this.authSession = authSession
    },
    signOut(): void {
      const { isWebMode } = useAppMode()
      const resetStores = useResetStores()
      const tracking = useTracking()

      gaEventOnUserSignOut()

      this.authSession = undefined
      this.authUser = undefined
      tracking.identifyUser(undefined)

      resetStores()

      if (isWebMode) {
        emitCartItemCount()
        emitSignedInState()
        emitUserTags()
      }
    }
  },
  persist: true
})

export default useAuthStore
export { initialState }
